import { CHAINS } from "./triggers";
import { TutorialRunner } from "./tutorial";

/**
 * STARTING AND FEEDING THE TUTORIAL. One runner for the whole app.
 *
 * ⚠️ **ONE RUNNER, NOT ONE PER CALLER.** Two runners would draw two spotlights and race each other
 * to advance on the same engine signal - and the second one's popup would sit over the first's,
 * which reads as the game having frozen.
 */

let runner: TutorialRunner | null = null;

/** The live runner, if a lesson is in progress. */
export function activeTutorial(): TutorialRunner | null {
  return runner;
}

async function runChain(entry: string): Promise<void> {
  const chain = CHAINS.find((c) => c.entry === entry);
  if (!chain) return;
  // ⚠️ A second call while one is running REPLACES it rather than stacking - the player clicked the
  // button again, which means "start over", not "show me two".
  runner?.stop();
  runner = new TutorialRunner();
  try {
    await runner.run(chain.start);
  } finally {
    runner = null;
  }
}

/**
 * The lobby tour: the deck builder, duels and leagues.
 *
 * 🚨 **THIS CHAIN COULD NEVER START.** Its head is `59_Lobby_FirstIronside`, entered on
 * `FirstIronside`, and the Ironside layer is CUT by decision - so a trigger that will never fire
 * gated all nineteen steps. `CHAINS` re-enters at `60_Lobby_DecksBtn`, which is where the tour
 * actually begins, so eighteen of them survive the cut instead of none.
 */
export function startLobbyTour(): Promise<void> {
  return runChain("LobbyTour");
}

/** The first-launch welcome. Two steps, then it waits for a match. */
export function startWelcome(): Promise<void> {
  return runChain("Start");
}

/**
 * A match has begun and the engine said `Welcome` - run the 32-step in-match lesson.
 *
 * ⚠️ Only for a player who has not finished it. The caller checks; this does not, because a
 * "have they done it" test buried in here would be a second owner of that decision.
 */
export function startMatchLesson(): Promise<void> {
  return runChain("Welcome");
}

/**
 * Feed an engine signal to whatever lesson is running.
 *
 * ⚠️ **SAFE TO CALL FOR EVERY SIGNAL.** Matching is by name, so an unrelated one is ignored rather
 * than advancing the tutorial past a lesson the player has not read.
 */
export function tutorialSignal(name: string): void {
  runner?.onSignal(name);
}
